import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { formatDate } from '@angular/common';
import { filter } from 'rxjs/operators';
import { ApiService, HomeAssistantResponse } from 'src/app/services/api.service';
import { AuthService } from 'src/app/services/auth.service';
import { StorageService } from 'src/app/services/storage.service';
import { PaymentService } from 'src/app/services/payment.service';

import { ChatService } from 'src/app/services/chat.service';

export interface ChatMessage {
  id: string;
  author: 'assistant' | 'user';
  text: string;
  time: string;
  type?: 'text' | 'menu' | 'branches' | 'departments' | 'doctors' | 'patient_form' | 'dates' | 'slots' | 'confirmation';
  data?: any;
}

type BookingStep = 'idle' | 'branch' | 'department' | 'doctor' | 'patient' | 'date' | 'slot' | 'complete';

@Component({
  selector: 'app-chat-widget',
  templateUrl: './chat-widget.component.html',
  styleUrls: ['./chat-widget.component.scss']
})
export class ChatWidgetComponent implements OnInit {
  @ViewChild('chatScroll') private chatScrollContainer!: ElementRef;

  get isOpen(): boolean {
    return this.chatService.isOpen;
  }
  set isOpen(val: boolean) {
    this.chatService.isOpen = val;
  }

  isTyping = false;
  isSubmittingBooking = false;
  userInput = '';
  step: BookingStep = 'idle';

  messages: ChatMessage[] = [];
  departments: any[] = [];
  branches: any[] = [];
  doctors: any[] = [];
  dates: string[] = [];
  slots: any[] = [];

  selectedDepartment: any = null;
  selectedBranch: any = null;
  selectedDoctor: any = null;
  selectedDate = '';
  selectedSlot: any = null;
  doctorBranchId: any = null;

  patientForm = this.fb.group({
    name: ['', [Validators.required, Validators.pattern('^[a-zA-Z ]{3,150}$')]],
    gender: ['', Validators.required],
    dob: ['', Validators.required],
    mrNumber: ['']
  });

  menuOptions = [
    { id: 'book', icon: 'calendar_month', title: 'Book an Appointment', subtitle: 'Find & schedule with a doctor' },
    { id: 'departments', icon: 'medical_services', title: 'Browse Specialities', subtitle: 'Explore clinical departments' },
    { id: 'doctors', icon: 'person_search', title: 'Find Doctors', subtitle: 'View all verified specialists' },
    { id: 'bookings', icon: 'receipt_long', title: 'Check My Bookings', subtitle: 'Track your scheduled visits' },
    { id: 'faq', icon: 'help_outline', title: 'Service Menu & FAQs', subtitle: 'Common questions & clinic info' }
  ];

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private authService: AuthService,
    private storage: StorageService,
    private router: Router,
    private paymentService: PaymentService,
    public chatService: ChatService
  ) {}

  ngOnInit(): void {
    this.initChat();
    this.loadBranchesData();
    this.loadDepartmentsData();
    this.chatService.onResetAfterPayment().subscribe(() => this.clearAfterPayment());
    this.authService.isAuthenticated.subscribe((authenticated) => {
      if (authenticated) {
        this.resumePendingBooking();
      }
    });

    // Open the assistant only on its dedicated route. A completed payment
    // starts with a clean, closed conversation.
    if (this.router.url.includes('/ai-booking')) {
      this.isOpen = true;
    } else if (this.router.url.includes('/booking-status/success')) {
      this.clearAfterPayment();
    }

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        if (event.url.includes('/ai-booking')) {
          this.isOpen = true;
          setTimeout(() => this.scrollToBottom(), 100);
        } else if (event.url.includes('/booking-status/success')) {
          this.clearAfterPayment();
        }
      });
  }

  initChat(): void {
    const timeNow = formatDate(new Date(), 'h:mm a', 'en-US');
    this.messages = [
      {
        id: 'msg-1',
        author: 'assistant',
        text: '👋 Hello! I am Mily, your MedEase Live Health Champion. How can I assist you today?',
        time: timeNow,
        type: 'text'
      },
      {
        id: 'msg-2',
        author: 'assistant',
        text: 'You can choose an option below or type naturally in the chat (e.g., “I need a skin doctor tomorrow”).',
        time: timeNow,
        type: 'menu'
      }
    ];
  }

  toggleChat(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      setTimeout(() => this.scrollToBottom(), 100);
    }
  }

  scrollToBottom(): void {
    try {
      if (this.chatScrollContainer) {
        this.chatScrollContainer.nativeElement.scrollTop = this.chatScrollContainer.nativeElement.scrollHeight;
      }
    } catch (_) {}
  }

  loadDepartmentsData(): void {
    this.apiService.getAllSpecialities().subscribe({
      next: (response: any) => {
        this.departments = response?.success ? response.data.data : [];
      },
      error: () => {}
    });
  }

  loadBranchesData(): void {
    this.apiService.getBranches().subscribe({
      next: (response: any) => {
        this.branches = response?.success ? (response.data.data || response.data || []) : [];
      },
      error: () => { this.branches = []; }
    });
  }

  handleMenuClick(optionId: string): void {
    const timeNow = formatDate(new Date(), 'h:mm a', 'en-US');
    switch (optionId) {
      case 'book':
        this.messages.push({
          id: `usr-${Date.now()}`,
          author: 'user',
          text: 'Book an Appointment',
          time: timeNow
        });
        this.startBookingFlow();
        break;

      case 'departments':
        this.messages.push({
          id: `usr-${Date.now()}`,
          author: 'user',
          text: 'Browse Specialities',
          time: timeNow
        });
        this.showDepartments();
        break;

      case 'doctors':
        this.messages.push({
          id: `usr-${Date.now()}`,
          author: 'user',
          text: 'Find Doctors',
          time: timeNow
        });
        this.messages.push({
          id: `ast-${Date.now()}`,
          author: 'assistant',
          text: 'Taking you to our doctors directory...',
          time: timeNow
        });
        setTimeout(() => this.router.navigate(['/doctors']), 700);
        break;

      case 'bookings':
        this.messages.push({
          id: `usr-${Date.now()}`,
          author: 'user',
          text: 'Check My Bookings',
          time: timeNow
        });
        if (this.authService.getToken()) {
          this.router.navigate(['/booking-list']);
        } else {
          this.messages.push({
            id: `ast-${Date.now()}`,
            author: 'assistant',
            text: 'Please log in to view your bookings and appointment history.',
            time: timeNow
          });
          setTimeout(() => this.router.navigate(['/login']), 1000);
        }
        break;

      case 'faq':
        this.messages.push({
          id: `usr-${Date.now()}`,
          author: 'user',
          text: 'Service Menu & FAQs',
          time: timeNow
        });
        this.router.navigate(['/faq']);
        break;
    }
    setTimeout(() => this.scrollToBottom(), 100);
  }

  startBookingFlow(): void {
    this.step = 'branch';
    const timeNow = formatDate(new Date(), 'h:mm a', 'en-US');
    this.showTyping();
    setTimeout(() => {
      this.isTyping = false;
      this.messages.push({
        id: `ast-${Date.now()}`,
        author: 'assistant',
        text: 'First, please select the branch where you want to book your appointment:',
        time: timeNow,
        type: 'branches',
        data: this.branches
      });
      this.scrollToBottom();
    }, 600);
  }

  chooseBranch(branch: any): void {
    this.selectedBranch = branch;
    const timeNow = formatDate(new Date(), 'h:mm a', 'en-US');
    this.messages.push({ id: `usr-${Date.now()}`, author: 'user', text: branch.name, time: timeNow });
    this.step = 'department';
    this.showTyping();
    this.apiService.getAllSpecialitiesForAssistant().subscribe({
      next: (response: any) => {
        this.isTyping = false;
        this.departments = response?.success ? (response.data.data || response.data || []) : [];
        this.messages.push({
          id: `ast-${Date.now()}`,
          author: 'assistant',
          text: `Now select a department. Doctors will be matched within ${branch.name}:`,
          time: formatDate(new Date(), 'h:mm a', 'en-US'),
          type: 'departments',
          data: this.departments
        });
        this.scrollToBottom();
      },
      error: () => {
        this.isTyping = false;
        this.messages.push({ id: `ast-${Date.now()}`, author: 'assistant', text: 'Unable to load departments for this branch.', time: formatDate(new Date(), 'h:mm a', 'en-US') });
        this.scrollToBottom();
      }
    });
  }

  showDepartments(): void {
    const timeNow = formatDate(new Date(), 'h:mm a', 'en-US');
    this.messages.push({
      id: `ast-${Date.now()}`,
      author: 'assistant',
      text: 'Here are the medical departments available at MedEase:',
      time: timeNow,
      type: 'departments',
      data: this.departments
    });
    setTimeout(() => this.scrollToBottom(), 100);
  }

  chooseDepartment(department: any): void {
    this.selectedDepartment = department;
    const timeNow = formatDate(new Date(), 'h:mm a', 'en-US');
    this.messages.push({
      id: `usr-${Date.now()}`,
      author: 'user',
      text: department.title,
      time: timeNow
    });

    this.step = 'doctor';
    this.showTyping();

    this.apiService.getBranchDoctorsList({ branchId: this.selectedBranch?.id, specialtyId: department.id }).subscribe({
      next: (response: any) => {
        this.isTyping = false;
        this.doctors = response?.success ? response.data.data : [];
        if (this.doctors && this.doctors.length > 0) {
          this.messages.push({
            id: `ast-${Date.now()}`,
            author: 'assistant',
            text: `Found ${this.doctors.length} doctor(s) in ${department.title}. Select a doctor to continue:`,
            time: formatDate(new Date(), 'h:mm a', 'en-US'),
            type: 'doctors',
            data: this.doctors
          });
        } else {
          this.messages.push({
            id: `ast-${Date.now()}`,
            author: 'assistant',
            text: `There are currently no doctors scheduled for ${department.title}. Please choose another specialty or ask me for alternatives.`,
            time: formatDate(new Date(), 'h:mm a', 'en-US'),
            type: 'menu'
          });
        }
        this.scrollToBottom();
      },
      error: () => {
        this.isTyping = false;
        this.messages.push({
          id: `ast-${Date.now()}`,
          author: 'assistant',
          text: 'Unable to retrieve doctors for this department at the moment.',
          time: formatDate(new Date(), 'h:mm a', 'en-US')
        });
        this.scrollToBottom();
      }
    });
  }

  chooseDoctor(doctor: any): void {
    this.selectedDoctor = doctor;
    const timeNow = formatDate(new Date(), 'h:mm a', 'en-US');

    this.messages.push({
      id: `usr-${Date.now()}`,
      author: 'user',
      text: `Dr. ${doctor.title}`,
      time: timeNow
    });

    this.step = 'patient';
    this.showTyping();

    // Resolve the doctor exactly as the normal booking page does so the
    // encrypted doctor and branch ids used for slot lookup are consistent.
    this.apiService.getDoctorDetails(doctor.id).subscribe({
      next: (response: any) => {
        this.isTyping = false;
        if (!response?.success || !response.data) {
          this.messages.push({
            id: `ast-${Date.now()}`,
            author: 'assistant',
            text: 'I could not load this doctor\'s booking details. Please try again.',
            time: formatDate(new Date(), 'h:mm a', 'en-US')
          });
          this.scrollToBottom();
          return;
        }

        this.selectedDoctor = response.data;
        const selectedBranch = this.selectedDoctor.branches?.find((item: any) =>
          item.branch?.id === this.selectedBranch?.id
        );
        this.doctorBranchId = selectedBranch?.branch?.id
          || this.selectedDoctor.branches?.[0]?.branch?.id
          || this.selectedBranch?.id;

        if (!this.doctorBranchId) {
          this.messages.push({
            id: `ast-${Date.now()}`,
            author: 'assistant',
            text: 'I could not find this doctor\'s branch details. Please try another doctor.',
            time: formatDate(new Date(), 'h:mm a', 'en-US')
          });
          this.scrollToBottom();
          return;
        }

        this.messages.push({
          id: `ast-${Date.now()}`,
          author: 'assistant',
          text: `Great choice! Please provide the patient details for the appointment with Dr. ${this.selectedDoctor.title}:`,
          time: formatDate(new Date(), 'h:mm a', 'en-US'),
          type: 'patient_form'
        });
        this.scrollToBottom();
      },
      error: () => {
        this.isTyping = false;
        this.messages.push({
          id: `ast-${Date.now()}`,
          author: 'assistant',
          text: 'I could not load this doctor\'s booking details. Please try again.',
          time: formatDate(new Date(), 'h:mm a', 'en-US')
        });
        this.scrollToBottom();
      }
    });
  }

  submitPatientDetails(): void {
    if (this.patientForm.invalid) {
      this.patientForm.markAllAsTouched();
      return;
    }
    const timeNow = formatDate(new Date(), 'h:mm a', 'en-US');
    this.messages.push({
      id: `usr-${Date.now()}`,
      author: 'user',
      text: `Patient: ${this.patientForm.value.name}`,
      time: timeNow
    });

    if (!this.authService.getToken()) {
      this.storage.set('ai-pending-booking', JSON.stringify({
        branch: this.selectedBranch,
        department: this.selectedDepartment,
        doctor: this.selectedDoctor,
        doctorBranchId: this.doctorBranchId,
        patient: this.patientForm.value
      }));
      this.isTyping = false;
      this.messages.push({
        id: `ast-${Date.now()}`,
        author: 'assistant',
        text: 'Please log in before I check live appointment dates and time slots.',
        time: formatDate(new Date(), 'h:mm a', 'en-US')
      });
      this.isOpen = false;
      setTimeout(() => this.router.navigate(['/login'], { queryParams: { returnUrl: this.router.url } }), 900);
      return;
    }

    this.loadAvailableDates();
  }

  private loadAvailableDates(): void {
    this.step = 'date';
    this.showTyping();

    // Omit month so the API returns every future date with available slots.
    this.apiService.getMonthSlots(null, false, this.selectedDoctor.id, this.doctorBranchId)
      .then((response: any) => {
        this.isTyping = false;
        this.dates = response?.success ? (response.data.date || []) : [];
        if (this.dates && this.dates.length > 0) {
          this.messages.push({
            id: `ast-${Date.now()}`,
            author: 'assistant',
            text: 'Please select your preferred appointment date:',
            time: formatDate(new Date(), 'h:mm a', 'en-US'),
            type: 'dates',
            data: this.dates
          });
        } else {
          this.messages.push({
            id: `ast-${Date.now()}`,
            author: 'assistant',
            text: 'There are no open dates for this doctor this month. You may try another specialist.',
            time: formatDate(new Date(), 'h:mm a', 'en-US'),
            type: 'menu'
          });
        }
        this.scrollToBottom();
      })
      .catch(() => {
        this.isTyping = false;
        this.messages.push({
          id: `ast-${Date.now()}`,
          author: 'assistant',
          text: 'I could not access the appointment calendar. Please confirm that the backend is running and try again.',
          time: formatDate(new Date(), 'h:mm a', 'en-US')
        });
        this.scrollToBottom();
      });
  }

  private resumePendingBooking(): void {
    const pending = JSON.parse(this.storage.get('ai-pending-booking') || '{}');
    if (!pending?.doctor || !pending?.doctorBranchId) {
      return;
    }

    this.storage.remove('ai-pending-booking');
    this.selectedBranch = pending.branch;
    this.selectedDepartment = pending.department;
    this.selectedDoctor = pending.doctor;
    this.doctorBranchId = pending.doctorBranchId;
    this.patientForm.patchValue(pending.patient || {});
    this.isOpen = true;
    this.messages.push({
      id: `ast-${Date.now()}`,
      author: 'assistant',
      text: 'Welcome back. Your patient details are saved, so I will continue with the available appointment dates.',
      time: formatDate(new Date(), 'h:mm a', 'en-US')
    });
    this.loadAvailableDates();
  }

  chooseDate(date: string): void {
    this.selectedDate = date;
    const timeNow = formatDate(new Date(), 'h:mm a', 'en-US');
    this.messages.push({
      id: `usr-${Date.now()}`,
      author: 'user',
      text: formatDate(date, 'EEEE, MMM d, y', 'en-US'),
      time: timeNow
    });

    this.step = 'slot';
    this.showTyping();

    this.apiService.getSlotsByDate(date, false, this.selectedDoctor.id, this.doctorBranchId, null, false).subscribe({
      next: (response: any) => {
        this.isTyping = false;
        const now = new Date();
        this.slots = response?.success
          ? response.data.filter(
              (slot: any) =>
                !slot.blocked &&
                !slot.is_break &&
                !slot.slotsLimited &&
                (!slot.time_limits ||
                  (now >= new Date(slot.time_limits.booking_start_time) &&
                    now <= new Date(slot.time_limits.booking_end_time)))
            )
          : [];

        if (this.slots && this.slots.length > 0) {
          this.messages.push({
            id: `ast-${Date.now()}`,
            author: 'assistant',
            text: `Available time slots on ${formatDate(date, 'MMM d', 'en-US')}:`,
            time: formatDate(new Date(), 'h:mm a', 'en-US'),
            type: 'slots',
            data: this.slots
          });
        } else {
          this.messages.push({
            id: `ast-${Date.now()}`,
            author: 'assistant',
            text: 'No available slots found on this date. Please pick a different date.',
            time: formatDate(new Date(), 'h:mm a', 'en-US'),
            type: 'dates',
            data: this.dates
          });
        }
        this.scrollToBottom();
      },
      error: () => {
        this.isTyping = false;
        this.messages.push({
          id: `ast-${Date.now()}`,
          author: 'assistant',
          text: 'Unable to load time slots right now.',
          time: formatDate(new Date(), 'h:mm a', 'en-US')
        });
        this.scrollToBottom();
      }
    });
  }

  chooseSlot(slot: any): void {
    this.selectedSlot = slot;
    const timeNow = formatDate(new Date(), 'h:mm a', 'en-US');
    const timeFormatted = formatDate(slot.start_time, 'h:mm a', 'en-US');

    this.messages.push({
      id: `usr-${Date.now()}`,
      author: 'user',
      text: `${timeFormatted} slot`,
      time: timeNow
    });

    this.step = 'complete';
    this.showTyping();

    setTimeout(() => {
      this.isTyping = false;
      this.messages.push({
        id: `ast-${Date.now()}`,
        author: 'assistant',
        text: 'Your appointment is ready to be confirmed! Review the summary below and click proceed to finalize your booking.',
        time: timeNow,
        type: 'confirmation',
        data: {
          doctor: this.selectedDoctor,
          date: this.selectedDate,
          slot: this.selectedSlot,
          patient: this.patientForm.value
        }
      });
      this.scrollToBottom();
    }, 500);
  }

  continueToBooking(): void {
    const doctorId = this.selectedDoctor?.id;
    const slotId = this.selectedSlot?.id;

    if (!doctorId || !slotId || !this.selectedDate) {
      this.messages.push({
        id: `ast-${Date.now()}`,
        author: 'assistant',
        text: 'The appointment details are incomplete. Please select the date and time slot again.',
        time: formatDate(new Date(), 'h:mm a', 'en-US')
      });
      this.scrollToBottom();
      return;
    }

    if (!this.authService.getToken()) {
      this.router.navigate(['/login']);
      return;
    }

    if (this.isSubmittingBooking) {
      return;
    }

    this.isSubmittingBooking = true;
    this.isTyping = true;
    const storedUser = JSON.parse(this.storage.get('user_data') || '{}');
    const user = storedUser?.data || storedUser;
    const patient = this.patientForm.value;
    const bookingPayload = {
      slot_id: slotId,
      no_of_adults: 1,
      no_of_childs: 0,
      mrnumber: patient.mrNumber || '',
      name: patient.name,
      dob: patient.dob,
      gender: patient.gender,
      phone: user?.phone,
      email: user?.email,
      revisit: false,
      whatsapp_notification: false
    };

    this.apiService.booking(bookingPayload).then((response: any) => {
      if (!response?.success || !response?.data) {
        throw new Error(response?.error?.message || response?.message || 'Unable to create the booking.');
      }

      const bookingDetails = {
        order_id: response.data.order_id,
        amount: response.data.amount,
        booking_id: response.data.booking_id,
        payment_method: '1',
        appointmentToken: response.data.appointmentToken,
        name: patient.name,
        email: user?.email,
        phone: user?.phone,
        doctor: response.data.doctor,
        hospital: response.data.hospital,
        booking_date: response.data.booking_date || this.selectedDate,
        slot_time: response.data.slot_time || this.selectedSlot.start_time
      };

      // The widget is global and fixed above routed pages; close it before
      // opening the existing Razorpay checkout.
      this.isOpen = false;
      this.isTyping = false;
      this.isSubmittingBooking = false;
      this.paymentService.pay(bookingDetails);
    }).catch((error: any) => {
      this.isTyping = false;
      this.isSubmittingBooking = false;
      this.messages.push({
        id: `ast-${Date.now()}`,
        author: 'assistant',
        text: error?.error?.message || error?.message || 'Unable to create the booking. Please try again.',
        time: formatDate(new Date(), 'h:mm a', 'en-US')
      });
      this.scrollToBottom();
    });
  }

  sendMessage(): void {
    const text = this.userInput.trim();
    if (!text || this.isTyping) {
      return;
    }

    const timeNow = formatDate(new Date(), 'h:mm a', 'en-US');
    this.messages.push({
      id: `usr-${Date.now()}`,
      author: 'user',
      text: text,
      time: timeNow
    });
    this.userInput = '';
    this.showTyping();
    this.scrollToBottom();

    // Natural Language processing via backend AI
    this.apiService.homeBookingAssistant(text).subscribe({
      next: (response: HomeAssistantResponse) => {
        this.isTyping = false;
        const reply = response?.data?.reply || response?.message || 'I understood your request.';
        const specialtyId = response?.data?.specialty_id;
        const specialty = response?.data?.specialty;

        this.messages.push({
          id: `ast-${Date.now()}`,
          author: 'assistant',
          text: reply,
          time: formatDate(new Date(), 'h:mm a', 'en-US')
        });

        if (specialtyId) {
          this.apiService.getBranchDoctorsList({ specialtyId }).subscribe({
            next: (docRes: any) => {
              const matchedDoctors = docRes?.success ? docRes.data.data : [];
              if (matchedDoctors.length > 0) {
                this.messages.push({
                  id: `ast-${Date.now()}`,
                  author: 'assistant',
                  text: `Here are available specialists for ${specialty || 'your condition'}:`,
                  time: formatDate(new Date(), 'h:mm a', 'en-US'),
                  type: 'doctors',
                  data: matchedDoctors
                });
              }
              this.scrollToBottom();
            },
            error: () => this.scrollToBottom()
          });
        } else {
          this.scrollToBottom();
        }
      },
      error: () => {
        this.isTyping = false;
        this.messages.push({
          id: `ast-${Date.now()}`,
          author: 'assistant',
          text: 'I am here to help. You can choose from our main service menu below or tell me which doctor you are looking for.',
          time: formatDate(new Date(), 'h:mm a', 'en-US'),
          type: 'menu'
        });
        this.scrollToBottom();
      }
    });
  }

  goToMainMenu(): void {
    const timeNow = formatDate(new Date(), 'h:mm a', 'en-US');
    this.messages.push({
      id: `ast-${Date.now()}`,
      author: 'assistant',
      text: 'Main Service Menu:',
      time: timeNow,
      type: 'menu'
    });
    setTimeout(() => this.scrollToBottom(), 100);
  }

  restart(): void {
    this.step = 'idle';
    this.selectedBranch = null;
    this.selectedDepartment = null;
    this.selectedDoctor = null;
    this.selectedSlot = null;
    this.selectedDate = '';
    this.doctors = [];
    this.dates = [];
    this.slots = [];
    this.patientForm.reset();
    this.initChat();
    setTimeout(() => this.scrollToBottom(), 100);
  }

  private clearAfterPayment(): void {
    this.isOpen = false;
    this.isTyping = false;
    this.isSubmittingBooking = false;
    this.step = 'idle';
    this.selectedBranch = null;
    this.selectedDepartment = null;
    this.selectedDoctor = null;
    this.selectedDate = '';
    this.selectedSlot = null;
    this.doctorBranchId = null;
    this.doctors = [];
    this.dates = [];
    this.slots = [];
    this.patientForm.reset();
    this.initChat();
  }

  private showTyping(): void {
    this.isTyping = true;
    setTimeout(() => this.scrollToBottom(), 50);
  }
}
